import React, { useState } from 'react'
import {
  Flex,
  useColorModeValue,
  Box,
  Stat,
  StatLabel,
  Switch,
  Text,
  Alert,
  AlertIcon,
  AlertTitle,
} from '@chakra-ui/react'
import {
  ResponsiveContainer,
  LineChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Line,
} from 'recharts'

import { getLocationsPercentage } from '../../../../utils/helpers'
import PercentageChange from '../../../reports/pages/components/EnergyReportComponents/PercentageChange'
import { convertToKW, convertToKWString, PERIODPARAMS } from '../../consts'
import { useQueryParams } from '../../hooks/useQueryParams'

interface LineChartCardProps {
  title: string
  unit: string
  value: number
  previousValue: number
  currentPeriod: { bucket: string; value: number }[]
  previousPeriod: { bucket: string; value: number }[]
}

export const LineChartCard: React.FC<LineChartCardProps> = ({
  title,
  unit,
  value = 0,
  previousValue = 0,
  currentPeriod = [],
  previousPeriod = [],
}) => {
  const [showPrevious, setShowPrevious] = useState(false)
  const bg = useColorModeValue('white', 'gray.800')
  const [queryParams] = useQueryParams(PERIODPARAMS)

  const fromDate: Date = new Date(queryParams.from)
  const toDate: Date = new Date(queryParams.to)

  const msPerDay = 1000 * 60 * 60 * 24

  const diffInDays: number = Math.round(
    (toDate.getTime() - fromDate.getTime()) / msPerDay,
  )

  const chartData =
    (showPrevious ? previousPeriod : currentPeriod)?.map((entry) => ({
      time:
        diffInDays >= 2
          ? new Date(entry.bucket).toLocaleDateString()
          : new Date(entry.bucket).toLocaleDateString(undefined, {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            }),
      value: convertToKW(entry.value, unit),
    })) ?? []

  const hasValidData = chartData.some((point) => typeof point.value === 'number')
  if ('TotalActiveEnergy' === title && currentPeriod.length && previousPeriod.length) {
    value = convertToKW(
      currentPeriod[currentPeriod.length - 1].value - currentPeriod[0].value,
      unit,
    )
    previousValue = convertToKW(
      previousPeriod[previousPeriod.length - 1]?.value - previousPeriod[0].value,
      unit,
    )
  }
  return (
    <Box bg={bg} p={4} borderRadius="lg" boxShadow="sm" w="100%" minH="350px">
      <Flex justify="space-between" align="center" mb={3}>
        <Stat>
          <StatLabel fontWeight={700} fontSize={14} color="#484848">
            {title}
          </StatLabel>
          <Flex
            gap={2}
            alignItems="center"
            fontWeight={700}
            fontSize="24px"
            color="#484848"
          >
            {showPrevious ? previousValue.toLocaleString() : value.toLocaleString()}{' '}
            <Flex gap={2}>
              <Text as="span" fontSize="15px" color="#9B9B9B" fontWeight={400}>
                {convertToKWString(unit)}
              </Text>
              <PercentageChange
                percentageChange={getLocationsPercentage(value, previousValue)}
              />
            </Flex>
          </Flex>
        </Stat>
        <Flex align="center" fontSize="xs" gap={2}>
          <Switch
            size="sm"
            isChecked={showPrevious}
            onChange={() => setShowPrevious(!showPrevious)}
          />
          <Text>Show previous</Text>
        </Flex>
      </Flex>

      <Box w="100%" h="80%">
        {hasValidData ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.1} />
              <XAxis dataKey="time" fontSize={10} />
              <YAxis fontSize={10} />
              <Tooltip formatter={(value: number) => value.toFixed(2)} />
              <Line
                type="monotone"
                dataKey="value"
                stroke="#3182ce"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <Flex justify="center" align="center" h="100%">
            <Alert
              status="info"
              bg="blue.50"
              borderRadius="md"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              height="100%"
              color="blue.800"
            >
              <AlertIcon boxSize="30px" mr={0} color="blue.500" />
              <AlertTitle mt={2} mb={1} fontSize="md" fontWeight="bold">
                No data available for the period
              </AlertTitle>
            </Alert>
          </Flex>
        )}
      </Box>
    </Box>
  )
}
